import React from 'react';
import {
  Button,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  MenuButton,
  makeStyles,
} from '@fluentui/react-components';
import {
  ArrowUp24Regular,
  ArrowDown24Regular,
  Edit24Regular,
  Delete24Regular,
  MoreHorizontal24Regular,
} from '@fluentui/react-icons';
import StatusBadge from './StatusBadge.tsx';

interface QuestionCardProps {
  index: number;
  total: number;
  text: string;
  type: string;
  isRequired?: boolean;
  instructions?: string | null;
  criteria?: string | null;
  onEdit?: () => void;
  onDelete?: () => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  disabled?: boolean;
}

const MOBILE = '@media (max-width: 768px)';

const useStyles = makeStyles({
  root: {
    backgroundColor: 'var(--color-surface)',
    border: '1px solid var(--color-border-light)',
    borderRadius: 'var(--radius-sm)',
    padding: 'var(--space-md)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 'var(--space-sm)',
    [MOBILE]: {
      flexDirection: 'column',
    },
  },
  content: {
    flex: 1,
    minWidth: 0,
  },
  meta: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--space-xs)',
    marginBottom: 'var(--space-xs)',
  },
  orderBadge: {
    width: '22px',
    height: '22px',
    borderRadius: '50%',
    backgroundColor: 'var(--undp-primary)',
    color: '#FFFFFF',
    fontSize: '11px',
    fontWeight: '600',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  questionText: {
    fontSize: '14px',
    fontWeight: '500',
    color: 'var(--color-text)',
    lineHeight: '1.4',
    wordWrap: 'break-word',
    whiteSpace: 'pre-wrap',
  },
  detail: {
    fontSize: '12px',
    color: 'var(--color-text-secondary)',
    lineHeight: '1.4',
    wordWrap: 'break-word',
    whiteSpace: 'pre-wrap',
    marginTop: 'var(--space-xs)',
  },
  actions: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    flexShrink: 0,
    [MOBILE]: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      alignSelf: 'flex-end',
    },
  },
  actionBtn: {
    minWidth: '28px',
    height: '28px',
    padding: '0',
  },
});

export default function QuestionCard({
  index, total, text, type, isRequired, instructions, criteria,
  onEdit, onDelete, onMoveUp, onMoveDown, disabled,
}: QuestionCardProps) {
  const s = useStyles();

  return (
    <div className={s.root}>
      <div className={s.content}>
        <div className={s.meta}>
          <span className={s.orderBadge}>{index + 1}</span>
          <StatusBadge status="info" label={type} />
          {isRequired && <StatusBadge status="danger" label="Required" />}
        </div>
        <div className={s.questionText}>{text}</div>
        {instructions && <div className={s.detail}>Instructions: {instructions}</div>}
        {criteria && <div className={s.detail}>Criteria: {criteria}</div>}
      </div>
      <div className={s.actions}>
        <Button appearance="subtle" size="small" icon={<ArrowUp24Regular />} className={s.actionBtn} onClick={onMoveUp} disabled={disabled || index === 0} />
        <Button appearance="subtle" size="small" icon={<ArrowDown24Regular />} className={s.actionBtn} onClick={onMoveDown} disabled={disabled || index === total - 1} />
        <Button appearance="subtle" size="small" icon={<Edit24Regular />} className={s.actionBtn} onClick={onEdit} disabled={disabled} />
        <Button appearance="subtle" size="small" icon={<Delete24Regular />} className={s.actionBtn} onClick={onDelete} disabled={disabled} style={{ color: 'var(--color-error)' }} />
      </div>
    </div>
  );
}
